/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _objectSpread from "@babel/runtime-corejs3/helpers/esm/objectSpread2";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/components/input-radio-group */
import React from 'react';
import { default as uniqueId } from 'react-html-id';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import { InputRenderer } from '../../utils/input-renderer.js';
import componentClassNames from './index.module.scss';
import { sophistication } from './index.sophistication.js';

/**
 * @typedef inputRadioGroupInternalPropsType
 * @property {string} id - Input ID.
 * @property {string} [value=''] - Input value.
 * @property {('valid'|'invalid'|'highlight'|'warning'|'missing'|'standBy'|null)} status - Input status.
 */

/**
 * @classdesc A beautiful radio input component (Internal).
 */
var InputRadioGroupInternal = /*#__PURE__*/function (_React$Component) {
  _inherits(InputRadioGroupInternal, _React$Component);

  var _super = _createSuper(InputRadioGroupInternal);

  /**
   * @description Constructor.
   * @param {(inputRadioGroupPropsType|inputRadioGroupInternalPropsType)} [props] - Component props.
   */
  function InputRadioGroupInternal(props) {
    var _this;

    _classCallCheck(this, InputRadioGroupInternal);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "nextUniqueId", void 0);

    uniqueId.enableUniqueIds(_assertThisInitialized(_this));
    sophistication.setup(_assertThisInitialized(_this));
    return _this;
  }
  /**
   * @instance
   * @description React lifecycle.
   * @returns {void} Void.
   */


  _createClass(InputRadioGroupInternal, [{
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      sophistication.destroy(this);
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this2 = this,
          _context;

      var _this$props = this.props,
          name = _this$props.name,
          label = _this$props.label,
          radios = _this$props.radios,
          value = _this$props.value,
          status = _this$props.status,
          style = _this$props.style,
          className = _this$props.className,
          customStyles = _this$props.customStyles,
          customClassNames = _this$props.customClassNames,
          customColors = _this$props.customColors,
          globalTheme = _this$props.globalTheme,
          onChange = _this$props.onChange,
          onBlur = _this$props.onBlur;
      var globalStyles = globalTheme ? InputRadioGroup.globalStyles[globalTheme] || {} : {};
      var globalClassNames = globalTheme ? InputRadioGroup.globalClassNames[globalTheme] || {} : {};
      var globalColors = globalTheme ? InputRadioGroup.globalColors[globalTheme] || {} : {};
      var jssClassNames = sophistication.getClassNames(this, {
        status: status,
        customColors: _objectSpread({}, globalColors, {}, customColors),
        customStyles: customStyles
      });
      var mergedStyles = {
        root: mergeStyles(globalStyles.root, customStyles.root, style),
        inputRow: mergeStyles(globalStyles.inputRow, customStyles.inputRow),
        title: mergeStyles(globalStyles.title, customStyles.title),
        inputWrapper: mergeStyles(globalStyles.inputWrapper, customStyles.inputWrapper)
      };
      var mergedClassNames = {
        root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
        inputRow: mergeClassNames(componentClassNames.inputRow, globalClassNames.inputRow, customClassNames.inputRow),
        input: mergeClassNames(componentClassNames.input, jssClassNames.input),
        label: mergeClassNames([componentClassNames.blink, status === 'highlight' || status === 'warning'], componentClassNames.label, jssClassNames.label),
        title: mergeClassNames(componentClassNames.title, globalClassNames.title, customClassNames.title),
        inputWrapper: mergeClassNames(componentClassNames.inputWrapper, globalClassNames.inputWrapper, customClassNames.inputWrapper)
      };
      return /*#__PURE__*/React.createElement("span", {
        style: mergedStyles.root,
        className: mergedClassNames.root
      }, _bindInstanceProperty(_context = function _context() {
        _newArrowCheck(this, _this2);

        var index = 0;
        var subId = '';
        var output = [];

        for (var i in radios) {
          if (!Object.prototype.hasOwnProperty.call(radios, i)) {
            continue;
          }

          subId = this.nextUniqueId();
          output.push( /*#__PURE__*/React.createElement("span", {
            key: index,
            style: mergedStyles.inputRow,
            className: mergedClassNames.inputRow
          }, /*#__PURE__*/React.createElement("span", {
            style: mergedStyles.inputWrapper,
            className: mergedClassNames.inputWrapper
          }, /*#__PURE__*/React.createElement("input", {
            id: subId,
            type: 'radio',
            name: name,
            value: i,
            checked: value === i,
            onChange: onChange,
            onBlur: onBlur,
            className: mergedClassNames.input
          }), /*#__PURE__*/React.createElement("label", {
            htmlFor: subId,
            className: mergedClassNames.label
          }, label || name)), /*#__PURE__*/React.createElement("span", {
            style: mergedStyles.title,
            className: mergedClassNames.title
          }, radios[i])));
          index++;
        }

        return output;
      }).call(_context, this)());
    }
  }]);

  return InputRadioGroupInternal;
}(React.Component);

/**
 * @typedef inputRadioGroupErrorMessagesType
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [mandatory=null] - Mandatory error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [match=null] - Match error message.
 */

/**
 * @typedef inputRadioGroupPropsType
 * @property {string} name - Input name.
 * @property {string} [label=''] - Input label.
 * @property {Object<string,*>} [radios={}] - Radios descriptor.
 * @property {boolean} [mandatory=false] - Input mandatory flag.
 * @property {boolean} [keepMissingStatus=false] - Keep missing status after user interaction.
 * @property {(string|null)} [match=null] - Name of input that has to be matched to correctly validate.
 * @property {inputRadioGroupErrorMessagesType} [errorMessages] - Validation error messages.
 * @property {*} [plugins=[]] - Input plugins.
 * @property {boolean} [deferValidation=true] - Defer input validation until there is an interaction.
 * @property {Function} [onValidationResult=null] - Callback for validationResult event.
 * @property {Function} [onChange=null] - Callback for change event.
 * @property {Function} [onBlur=null] - Callback for blur event.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {Object<string,*>} [customColors=null] - Custom colors prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A beautiful radio input component.
 * @param {inputRadioGroupPropsType} props - Component props.
 * @returns {Object<string,*>} React component.
 */
function InputRadioGroup(props) {
  var _this3 = this,
      _context2;

  var name = props.name,
      mandatory = props.mandatory,
      keepMissingStatus = props.keepMissingStatus,
      match = props.match,
      errorMessages = props.errorMessages,
      plugins = props.plugins,
      deferValidation = props.deferValidation,
      onValidationResult = props.onValidationResult,
      onChange = props.onChange,
      onBlur = props.onBlur,
      other = _objectWithoutProperties(props, ["name", "mandatory", "keepMissingStatus", "match", "errorMessages", "plugins", "deferValidation", "onValidationResult", "onChange", "onBlur"]);

  return /*#__PURE__*/React.createElement(InputRenderer, {
    name: name,
    value: '',
    mandatory: mandatory,
    keepMissingStatus: keepMissingStatus,
    match: match,
    errorMessages: errorMessages,
    plugins: plugins,
    deferValidation: deferValidation,
    onValidationResult: onValidationResult,
    onChange: onChange,
    onBlur: onBlur,
    inputRenderFn: _bindInstanceProperty(_context2 = function _context2(inputProps, inputRendererAPI) {
      _newArrowCheck(this, _this3);

      return /*#__PURE__*/React.createElement(InputRadioGroupInternal, _extends({}, other, {
        name: inputProps.name,
        id: inputProps.id,
        value: inputProps.value,
        onChange: inputProps.onChange,
        onBlur: inputProps.onBlur,
        status: inputRendererAPI.status
      }));
    }).call(_context2, this)
  });
}

InputRadioGroup.defaultProps = {
  label: '',
  radios: {},
  mandatory: true,
  keepMissingStatus: false,
  match: null,
  plugins: [],
  errorMessages: {
    mandatory: null,
    match: null
  },
  deferValidation: true,
  onValidationResult: null,
  onChange: null,
  onBlur: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  customColors: {},
  globalTheme: 'default'
};
InputRadioGroup.globalStyles = {
  default: {
    root: {},
    inputRow: {},
    title: {},
    inputWrapper: {}
  }
};
InputRadioGroup.globalClassNames = {
  default: {
    root: '',
    inputRow: '',
    title: '',
    inputWrapper: ''
  }
};
InputRadioGroup.globalColors = {
  default: {}
};
export { InputRadioGroup };